/**
 * Shared document upload UI for feature-owned upload flows.
 * PARENT-CHILD CONTRACT: this component owns only the consistent dropzone,
 * pending rows, uploaded document cards, and action buttons. Parent screens
 * still upload files, store document payloads, and implement preview/download
 * behavior through the emitted events.
 */
import { ChangeDetectionStrategy, Component, input, output, signal } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';

import { UiDocumentUploadItem, UiUploadedDocumentItem } from './ui-document-upload.types';

/**
 * COMPONENT: Ui Document Upload
 * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
 * Output: Returns the documented value or updates component/service state without changing the public contract.
 * Processing: Documents the Angular declaration that connects this class to templates, dependency injection, or template transforms.
 */
@Component({
  selector: 'ui-document-upload',
  standalone: true,
  imports: [TranslatePipe],
  templateUrl: './ui-document-upload.component.html',
  styleUrl: './ui-document-upload.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiDocumentUploadComponent {
  /** STATE OWNER: dropActive documents the signal state boundary used by this file. */
  readonly dropActive = signal(false);

  /** PARENT-CHILD CONTRACT: accept documents the input state boundary used by this file. */
  readonly accept = input('');
  /** PARENT-CHILD CONTRACT: multiple documents the input state boundary used by this file. */
  readonly multiple = input(true);
  /** PARENT-CHILD CONTRACT: disabled documents the input state boundary used by this file. */
  readonly disabled = input(false);
  /** PARENT-CHILD CONTRACT: dropLabel documents the input state boundary used by this file. */
  readonly dropLabel = input('shared.form.dropFiles');
  /** PARENT-CHILD CONTRACT: pendingLabel documents the input state boundary used by this file. */
  readonly pendingLabel = input('shared.form.uploadingFile');
  /** PARENT-CHILD CONTRACT: imageLabel documents the input state boundary used by this file. */
  readonly imageLabel = input('shared.form.image');
  /** PARENT-CHILD CONTRACT: documentLabel documents the input state boundary used by this file. */
  readonly documentLabel = input('shared.form.document');
  /** PARENT-CHILD CONTRACT: emptyLabel documents the input state boundary used by this file. */
  readonly emptyLabel = input('shared.form.noFilesUploaded');
  /** PARENT-CHILD CONTRACT: removeActionLabel documents the input state boundary used by this file. */
  readonly removeActionLabel = input('shared.form.removeFileAction');
  /** PARENT-CHILD CONTRACT: downloadActionLabel documents the input state boundary used by this file. */
  readonly downloadActionLabel = input('shared.form.downloadFileAction');
  /** PARENT-CHILD CONTRACT: previewActionLabel documents the input state boundary used by this file. */
  readonly previewActionLabel = input('shared.form.previewFileAction');
  readonly pendingDocuments = input<UiDocumentUploadItem[]>([]);
  readonly documents = input<UiUploadedDocumentItem[]>([]);

  readonly filesSelected = output<File[]>();
  readonly removeDocument = output<string>();
  readonly downloadDocument = output<UiUploadedDocumentItem>();
  readonly previewDocument = output<UiUploadedDocumentItem>();

  /**
   * BUSINESS LOGIC: onFileSelected
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  onFileSelected(event: Event): void {
    const inputElement = event.target as HTMLInputElement;

    if (!inputElement.files) {
      return;
    }

    this.emitFiles(Array.from(inputElement.files));
    inputElement.value = '';
  }

  /**
   * BUSINESS LOGIC: onDragOver
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  onDragOver(event: DragEvent): void {
    if (this.disabled()) {
      return;
    }

    event.preventDefault();
    this.dropActive.set(true);
  }

  /**
   * BUSINESS LOGIC: onDragLeave
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.dropActive.set(false);
  }

  /**
   * BUSINESS LOGIC: onDrop
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  onDrop(event: DragEvent): void {
    if (this.disabled()) {
      return;
    }

    event.preventDefault();
    this.dropActive.set(false);
    const droppedFiles = Array.from(event.dataTransfer?.files ?? []);
    this.emitFiles(droppedFiles);
  }

  /**
   * BUSINESS LOGIC: fileIcon
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  fileIcon(type: UiDocumentUploadItem['type']): string {
    return type === 'image' ? 'image' : 'description';
  }

  /**
   * BUSINESS LOGIC: emitFiles
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private emitFiles(files: File[]): void {
    const selectedFiles = this.multiple() ? files : files.slice(0, 1);

    if (selectedFiles.length === 0) {
      return;
    }

    this.filesSelected.emit(selectedFiles);
  }
}
